import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'next/router'
import * as Cookies from 'js-cookie'
import { useSelector, useDispatch } from 'react-redux'
import { get } from 'lodash'
import io from 'socket.io-client'

import API from 'api'
import { clearAuthentication } from 'redux-web/utils/authentication/actions'
import { getPasswordDefinitions } from 'models/password'
import { selectCountry } from 'redux-web/selectors/countries'
import { setCountry } from 'redux-web/utils/countries/actions'
import { setSession, removeAccessToken } from 'redux-web/utils/session/actions'
import { setUser } from 'redux-web/utils/user/actions'
import { showBasicModal } from 'helpers/modal'
import { removeGuestSession } from 'helpers/guest'
import { hideModal, showModal } from 'components/Atoms/Modal/actions'
import { useTranslation } from 'i18n-web/i18next'
import {
  nationalPhoneNumber,
  internationalPhoneNumber,
} from 'helpers/phoneFormat'

import SignUpForm from './Form'

const SignUpFormHOC = props => {
  const { router, setVerifyEmailMessage, setVerifiedEmailMessage, queryData } =
    props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const countries = useSelector(state => state.countries)
  const loggedIn = useSelector(state => state.session.loggedIn)
  const user = useSelector(state => state.user.me)
  const error = useSelector(state => state.authentication.error)
  const isSDK = useSelector(state => state.settings.isSDK)

  const [phoneToken, setPhoneToken] = useState(null)
  const [formikRef, setFormikRef] = useState(null)
  const [passwordRules, setPasswordRules] = useState({})

  const query = useMemo(() => router.query, [router])

  const { application } = query

  const facebookToken = useMemo(() => get(query, 'facebookToken'), [query])
  const appleLoginToken = useMemo(
    () => get(query, 'apple_login_token'),
    [query]
  )
  const appleNameAvailable = useMemo(
    () => get(query, 'name_available'),
    [query]
  )

  const emailVerificationToken = useMemo(
    () => get(queryData, 'emailVerificationToken'),
    [queryData]
  )

  const phoneVerificationToken = useMemo(
    () => get(queryData, 'phoneVerificationToken'),
    [queryData]
  )

  const countryCode = useMemo(() => {
    const guestCountryCode = user.isGuest && user.phoneCountryCode

    return (
      get(queryData, 'phoneCountryCode') ||
      query.countryCode ||
      guestCountryCode ||
      countries.country
    )
  }, [queryData, query, countries, user])

  const countryData = useMemo(
    () => selectCountry(countries, countryCode),
    [countries, countryCode]
  )

  const hasNamePronunciation = useMemo(
    () => get(countryData, 'hasUserNamePronunciation'),
    [countryData]
  )

  const normalizePhoneNumber = phoneNumber =>
    internationalPhoneNumber(phoneNumber, countryCode)

  const initialValues = useMemo(
    () => ({
      name: get(queryData, 'name') || get(query, 'name_available') || '',
      email: get(queryData, 'email') || get(query, 'email_available') || '',
      password: '',
      confirmPassword: '',
      namePronunciation: '',
      phoneNumber:
        normalizePhoneNumber(get(queryData, 'phoneNumber')) ||
        normalizePhoneNumber(query.phoneNumber) ||
        '',
      discountCode: '',
      acceptedTerms: false,
      saveCards: false,
    }),
    [queryData, query]
  )

  const formikInstance = ref => setFormikRef(ref)

  useEffect(() => {
    if (appleLoginToken) dispatch(hideModal())
  }, [appleLoginToken])

  useEffect(() => {
    API()
      .passwordRules({ ...(isSDK && { sdk: true }) })
      .then(res => setPasswordRules(res.passwordRules))
      .catch(err => console.error(err))
  }, [])

  useEffect(() => {
    if (formikRef && user.isGuest) {
      formikRef.setFieldValue(
        'phoneNumber',
        normalizePhoneNumber(user.phoneNumber)
      )
    }
  }, [formikRef, user])

  useEffect(() => {
    const facebookRedirect = Cookies.get('citifyd_facebook_login_redirect')

    if (facebookToken && facebookRedirect) {
      router.push(
        `${facebookRedirect}?facebookToken=${facebookToken}`,
        undefined,
        {
          shallow: true,
        }
      )
      Cookies.remove('citifyd_facebook_login_redirect')
    }
  }, [facebookToken])

  useEffect(() => {
    if (formikRef && facebookToken) {
      formikRef.setStatus(null)
    }
  }, [formikRef])

  useEffect(() => {
    dispatch(setCountry(countryCode))
  }, [countryCode])

  const onSignUpSuccess = newUser => {
    // DELETE ACCESS TOKEN AFTER CREATE AN ACCOUNT SAVING THE CARD
    if (user.isGuest) {
      removeAccessToken()
    }

    removeGuestSession()
    dispatch(setSession(newUser))
    dispatch(setUser(newUser))
  }

  const onTermsClick = () => {
    dispatch(
      showModal({
        modalProps: {
          open: true,
          customStyle: { width: '580px' },
          className: 'noBorders',
        },
        modalType: 'termsAndConditions',
      })
    )
  }

  const onSignUpSubmit = (values, setStatus, setSubmitting) => {
    // DO NOT DELETE ACCESS TOKEN BEFORE CREATE AN ACCOUNT SAVING THE CARD
    if (!user.isGuest && !query?.saveCards) {
      removeAccessToken()
    }

    API()
      .createAccount({
        timeoutTryAgainMessage: true,
        ...(isSDK && { sdk: true }),
        body: JSON.stringify({
          ...values,
        }),
      })
      .then(resp => {
        setSubmitting(false)
        if (resp.error && resp.error.message) {
          setStatus({ message: resp.error.message })
          dispatch(
            showBasicModal({
              title: t('signUp.form.errors.title'),
              description: resp.error.message,
            })
          )
        } else if (resp.user) {
          if (
            resp.user.emailValidationRequired &&
            resp.user.ticketMigrationsAvailable
          ) {
            waitForEmailVerification(resp.user)
          } else {
            onSignUpSuccess(resp.user)
          }
        }
      })
      .catch(error => {
        setSubmitting(false)
        setStatus({ message: error })
        dispatch(
          showBasicModal({
            title: t('signUp.form.errors.title'),
            description: error,
          })
        )
      })
  }

  const getPhoneNumber = async (phoneNumber, setStatus, setSubmitting) => {
    await API()
      .getPhoneNumber(countryCode, phoneNumber, {
        timeoutTryAgainMessage: true,
        ...(isSDK && { sdk: true }),
      })
      .then(resp => {
        const phoneNumberStatus = resp.phoneNumber

        setSubmitting(false)
        if (resp?.error?.code === 408) {
          dispatch(
            showBasicModal({
              title: resp.error.message,
            })
          )
        } else {
          if (phoneNumberStatus.userExists) {
            setStatus({ message: t('signUp.form.errors.userExists') })
            dispatch(
              showBasicModal({
                title: t('signUp.form.errors.title'),
                description: t('signUp.form.errors.userExists'),
              })
            )
          } else {
            sendCodeVerification(phoneNumber, setStatus, setSubmitting)
          }
        }
      })
      .catch(err => {
        setStatus({ message: err })
        dispatch(
          showBasicModal({
            title: t('signUp.form.errors.title'),
            description: err,
          })
        )
        setSubmitting(false)
      })
  }

  const sendCodeVerification = async (
    phoneNumber,
    setStatus,
    setSubmitting
  ) => {
    await API()
      .sendPhoneVerification({
        ...(isSDK && { sdk: true }),
        timeoutTryAgainMessage: true,
        body: JSON.stringify({
          method: 'textMessage',
          phoneCountryCode: countryCode,
          phoneNumber: phoneNumber,
        }),
      })
      .then(resp => {
        setSubmitting(false)
        if (resp.success) {
          dispatch(
            showModal({
              childProps: {
                countryCode: countryCode,
                phoneNumber: phoneNumber,
                modalCallback: token => {
                  setPhoneToken(token)
                  formikRef.submitForm()
                },
              },
              fullScreen: false,
              modalProps: {
                open: true,
              },
              modalType: 'verifyPhoneNumber',
            })
          )
        } else {
          setStatus({ message: resp.error && resp.error.message })
          dispatch(
            showBasicModal({
              title: t('signUp.form.errors.title'),
              description: resp.error && resp.error.message,
            })
          )
        }
      })
      .catch(err => {
        setSubmitting(false)
        setStatus({ message: err })
        dispatch(
          showBasicModal({
            title: t('signUp.form.errors.title'),
            description: err,
          })
        )
      })
  }

  const handleSubmit = (values, { setSubmitting, setStatus }) => {
    setSubmitting(true)
    setStatus(null)

    if (phoneToken || (phoneVerificationToken && emailVerificationToken)) {
      const phoneNumber = nationalPhoneNumber(values.phoneNumber, countryCode)
      const data = {
        ...values,
        phoneNumber,
        phoneCountryCode: countryCode?.toLowerCase(),
        phoneVerificationToken: phoneToken || phoneVerificationToken,
        facebookToken: facebookToken,
        appleLoginToken: appleLoginToken,
        emailVerificationToken: emailVerificationToken,
        namePronunciation: values.namePronunciation
          ? values.namePronunciation
          : false,
      }

      onSignUpSubmit(data, setStatus, setSubmitting)
    } else {
      const phoneNumber = nationalPhoneNumber(values.phoneNumber, countryCode)

      getPhoneNumber(phoneNumber, setStatus, setSubmitting)
    }
  }

  const waitForEmailVerification = user => {
    setVerifyEmailMessage(true)
    onSignUpSuccess(user)
    const socket = io.connect(process.env.NEXT_PUBLIC_API_SOCKET_URL, {
      reconnectionAttempts: 3,
    })

    socket.on('connect', () => {
      socket.emit('authenticate', { accessToken: user.accessToken })
    })

    socket.on('email verified', () => {
      setVerifiedEmailMessage(true)
      setVerifyEmailMessage(false)
      API().magicLink({ ...(isSDK && { sdk: true }) })
    })
  }

  const onFocusField = setStatus => {
    setStatus(null)
    dispatch(clearAuthentication())
  }

  const validatePassword = password => {
    password = (password || '').toString()

    const validations = [...passwordRules.features, 'minimum']

    let result = { missingFeatures: [] }

    for (const feature of validations) {
      const { pattern } = getPasswordDefinitions[feature]

      if (!pattern.test(password)) {
        result.missingFeatures.push(feature)
      }
    }

    setPasswordRules({
      ...passwordRules,
      ...result,
    })
  }

  const handlePasswordOnKeyUp = e => validatePassword(e.target.value)

  return (
    <SignUpForm
      appleLoginToken={appleLoginToken}
      appleNameAvailable={appleNameAvailable}
      application={application}
      country={countryCode}
      error={error}
      facebookToken={facebookToken}
      formikRef={formikInstance}
      handlePasswordOnKeyUp={handlePasswordOnKeyUp}
      handleSubmit={handleSubmit}
      hasNamePronunciation={hasNamePronunciation}
      initialValues={initialValues}
      loggedIn={loggedIn}
      onFocusField={onFocusField}
      onTermsClick={onTermsClick}
      passwordRules={passwordRules}
      setPhoneToken={setPhoneToken}
      showSaveCardCheckbox={Boolean(query?.saveCards)}
      setFieldValue={formikRef?.setFieldValue}
      t={t}
      {...props}
    />
  )
}

SignUpFormHOC.propTypes = {
  router: PropTypes.object,
  setVerifyEmailMessage: PropTypes.func,
  setVerifiedEmailMessage: PropTypes.func,
  queryData: PropTypes.any,
}

export default withRouter(SignUpFormHOC)
